/**
 * override-store.ts — Helix Model v2 Override System
 *
 * Manager-configurable feature. Three modes:
 *   off           — no overrides, system score only
 *   rep_allowed   — reps file and self-approve
 *   manager_only  — reps request, manager approves
 *
 * localStorage keys:
 *   aip:overrides:v1        — Override[]
 *   aip:override-policy:v1  — OverridePolicy
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type OverrideReason =
  | "verbal_commit"
  | "existing_expansion"
  | "internal_processes"
  | "exec_sponsor"
  | "legal_review"
  | "champion_guarantee";

export const OVERRIDE_REASON_LABELS: Record<OverrideReason, string> = {
  verbal_commit:       "Verbal commit from authority",
  existing_expansion:  "Existing customer expansion",
  internal_processes:  "Internal processes pending only",
  exec_sponsor:        "Executive sponsor commitment",
  legal_review:        "Contract in active legal review",
  champion_guarantee:  "Champion guarantee",
};

export const OVERRIDE_REASON_RISK: Record<OverrideReason, string> = {
  verbal_commit:       "Verbal is not signed — can change at any time.",
  existing_expansion:  "Account-level risk still applies (chapter 11, exec change).",
  internal_processes:  "External events can still kill (budget pulled, leadership change).",
  exec_sponsor:        "Sponsor credibility on the line — sponsor leaves and deal dies.",
  legal_review:        "Legal can still kill on terms.",
  champion_guarantee:  "Champion's credibility, not the company's.",
};

export type OverrideStatus = "pending" | "approved" | "denied" | "resolved";

export type OverrideResolution = "closed_on_date" | "slipped" | "lost";

export interface Override {
  id: string;
  deal_id: string;
  deal_name: string;
  filed_by: string;          // rep user id or name
  filed_by_name: string;     // display name
  filed_at: string;          // ISO timestamp
  reason: OverrideReason;
  reason_label: string;
  residual_risk_text: string; // auto-populated from OVERRIDE_REASON_RISK
  close_date_commitment: string; // ISO date
  justification: string;
  deal_value_usd?: number;
  approved_by?: string;
  approved_at?: string;
  status: OverrideStatus;
  resolution?: OverrideResolution;
  resolved_at?: string;
}

export type OverrideMode = "off" | "rep_allowed" | "manager_only";

export interface OverridePolicy {
  mode: OverrideMode;
  allowed_reasons: OverrideReason[];
  cosign_threshold_usd: number;
  default_max_close_days: number;
}

// ─── Storage keys ─────────────────────────────────────────────────────────────

const KEY_OVERRIDES = "aip:overrides:v1";
const KEY_POLICY    = "aip:override-policy:v1";

// ─── Default policy ───────────────────────────────────────────────────────────

export const DEFAULT_OVERRIDE_POLICY: OverridePolicy = {
  mode: "rep_allowed",
  allowed_reasons: [
    "verbal_commit",
    "existing_expansion",
    "internal_processes",
    "exec_sponsor",
    "legal_review",
    "champion_guarantee",
  ],
  cosign_threshold_usd: 0,
  default_max_close_days: 45,
};

// ─── Policy read / write ──────────────────────────────────────────────────────

export function loadOverridePolicy(): OverridePolicy {
  if (typeof window === "undefined") return DEFAULT_OVERRIDE_POLICY;
  try {
    const raw = localStorage.getItem(KEY_POLICY);
    return raw ? (JSON.parse(raw) as OverridePolicy) : DEFAULT_OVERRIDE_POLICY;
  } catch {
    return DEFAULT_OVERRIDE_POLICY;
  }
}

export function saveOverridePolicy(policy: OverridePolicy): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(KEY_POLICY, JSON.stringify(policy));
}

// ─── Override CRUD ────────────────────────────────────────────────────────────

export function loadOverrides(): Override[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY_OVERRIDES);
    return raw ? (JSON.parse(raw) as Override[]) : [];
  } catch {
    return [];
  }
}

function saveOverrides(list: Override[]): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(KEY_OVERRIDES, JSON.stringify(list));
}

// ─── File a new override ──────────────────────────────────────────────────────

export interface FileOverrideInput {
  deal_id: string;
  deal_name: string;
  filed_by: string;
  filed_by_name: string;
  reason: OverrideReason;
  close_date_commitment: string;
  justification: string;
  deal_value_usd?: number;
}

export function fileOverride(input: FileOverrideInput): Override {
  const policy = loadOverridePolicy();

  const override: Override = {
    id: `ov_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    deal_id: input.deal_id,
    deal_name: input.deal_name,
    filed_by: input.filed_by,
    filed_by_name: input.filed_by_name,
    filed_at: new Date().toISOString(),
    reason: input.reason,
    reason_label: OVERRIDE_REASON_LABELS[input.reason],
    residual_risk_text: OVERRIDE_REASON_RISK[input.reason],
    close_date_commitment: input.close_date_commitment,
    justification: input.justification,
    deal_value_usd: input.deal_value_usd,
    // In rep_allowed mode, auto-approve; in manager_only, stay pending
    status: policy.mode === "rep_allowed" ? "approved" : "pending",
    approved_by: policy.mode === "rep_allowed" ? input.filed_by : undefined,
    approved_at: policy.mode === "rep_allowed" ? new Date().toISOString() : undefined,
  };

  const list = loadOverrides();
  list.push(override);
  saveOverrides(list);
  return override;
}

// ─── Approve an override ──────────────────────────────────────────────────────

export function approveOverride(
  id: string,
  approved_by: string
): Override | null {
  const list = loadOverrides();
  const idx = list.findIndex((o) => o.id === id);
  if (idx < 0) return null;
  list[idx] = {
    ...list[idx],
    status: "approved",
    approved_by,
    approved_at: new Date().toISOString(),
  };
  saveOverrides(list);
  return list[idx];
}

// ─── Deny an override ─────────────────────────────────────────────────────────

export function denyOverride(id: string, denied_by: string): Override | null {
  const list = loadOverrides();
  const idx = list.findIndex((o) => o.id === id);
  if (idx < 0) return null;
  list[idx] = {
    ...list[idx],
    status: "denied",
    approved_by: denied_by,
    approved_at: new Date().toISOString(),
  };
  saveOverrides(list);
  return list[idx];
}

// ─── Resolve an override ──────────────────────────────────────────────────────

export function resolveOverride(
  id: string,
  resolution: OverrideResolution
): Override | null {
  const list = loadOverrides();
  const idx = list.findIndex((o) => o.id === id);
  if (idx < 0) return null;
  list[idx] = {
    ...list[idx],
    status: "resolved",
    resolution,
    resolved_at: new Date().toISOString(),
  };
  saveOverrides(list);
  return list[idx];
}

// ─── Query helpers ────────────────────────────────────────────────────────────

export function getOverridesForDeal(deal_id: string): Override[] {
  return loadOverrides().filter((o) => o.deal_id === deal_id);
}

/**
 * Returns active (approved, not yet resolved) overrides — the "live commits".
 */
export function getActiveOverrideCommits(): Override[] {
  return loadOverrides().filter((o) => o.status === "approved");
}

/**
 * Per-AE override accuracy report.
 * Returns resolved overrides grouped by rep with accuracy metrics.
 */
export interface RepOverrideAccuracy {
  rep_id: string;
  rep_name: string;
  total_filed: number;
  resolved: number;
  closed_on_date: number;
  slipped: number;
  lost: number;
  accuracy_pct: number; // closed_on_date / resolved * 100
}

export function getOverrideAccuracyByRep(): RepOverrideAccuracy[] {
  const all = loadOverrides();
  const repMap: Record<string, RepOverrideAccuracy> = {};

  for (const o of all) {
    if (!repMap[o.filed_by]) {
      repMap[o.filed_by] = {
        rep_id: o.filed_by,
        rep_name: o.filed_by_name,
        total_filed: 0,
        resolved: 0,
        closed_on_date: 0,
        slipped: 0,
        lost: 0,
        accuracy_pct: 0,
      };
    }
    const r = repMap[o.filed_by];
    r.total_filed += 1;
    if (o.status === "resolved" && o.resolution) {
      r.resolved += 1;
      if (o.resolution === "closed_on_date") r.closed_on_date += 1;
      else if (o.resolution === "slipped") r.slipped += 1;
      else if (o.resolution === "lost") r.lost += 1;
    }
  }

  for (const r of Object.values(repMap)) {
    r.accuracy_pct =
      r.resolved > 0 ? Math.round((r.closed_on_date / r.resolved) * 100) : 0;
  }

  return Object.values(repMap).sort((a, b) => b.total_filed - a.total_filed);
}

/**
 * Forecast Trust Index: system vs override breakdown.
 */
export interface ForecastTrustIndex {
  total_active_commits: number;
  system_commits: number;      // active non-override (deal has no active override)
  override_commits: number;    // deals with active override
  override_pct: number;        // override_commits / total * 100
  total_override_value_usd: number;
  override_accuracy_pct: number; // historical accuracy across all reps
}

export function getForecastTrustIndex(): ForecastTrustIndex {
  const active = getActiveOverrideCommits();
  const resolved = loadOverrides().filter((o) => o.status === "resolved" && o.resolution);
  const closedOnDate = resolved.filter((o) => o.resolution === "closed_on_date").length;
  const accuracy = resolved.length > 0
    ? Math.round((closedOnDate / resolved.length) * 100)
    : 0;

  const totalValue = active.reduce((s, o) => s + (o.deal_value_usd ?? 0), 0);

  return {
    total_active_commits: active.length,
    system_commits: 0, // placeholder — would come from deal pipeline data
    override_commits: active.length,
    override_pct: 100,
    total_override_value_usd: totalValue,
    override_accuracy_pct: accuracy,
  };
}

// ─── Demo seed ────────────────────────────────────────────────────────────────

function addDays(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Seeds 5 demo overrides across the team roster.
 * Matches rep names from reporting-calc.ts TEAM_ROSTER.
 * Only seeds if localStorage has no overrides yet.
 */
export function seedDemoOverridesIfEmpty(): void {
  if (typeof window === "undefined") return;
  const existing = loadOverrides();
  if (existing.length > 0) return;

  const today = todayISO();

  const demos: Override[] = [
    {
      id: "demo_ov_1",
      deal_id: "deal_001",
      deal_name: "Acme Manufacturing — Platform Expansion",
      filed_by: "rep2@mi6demo.example.com",
      filed_by_name: "Demo AE 2",
      filed_at: new Date(today + "T09:30:00Z").toISOString(),
      reason: "verbal_commit",
      reason_label: OVERRIDE_REASON_LABELS["verbal_commit"],
      residual_risk_text: OVERRIDE_REASON_RISK["verbal_commit"],
      close_date_commitment: addDays(today, 12),
      justification: "VP Operations verbally committed in Thursday's review meeting. They confirmed budget approval cleared and legal is a formality at this point. CFO was in the room.",
      deal_value_usd: 185000,
      status: "approved",
      approved_by: "manager",
      approved_at: new Date(today + "T10:00:00Z").toISOString(),
    },
    {
      id: "demo_ov_2",
      deal_id: "deal_002",
      deal_name: "Ferrix Aerospace — Compliance Suite",
      filed_by: "rep3@mi6demo.example.com",
      filed_by_name: "Demo AE 1",
      filed_at: new Date(today + "T11:00:00Z").toISOString(),
      reason: "existing_expansion",
      reason_label: OVERRIDE_REASON_LABELS["existing_expansion"],
      residual_risk_text: OVERRIDE_REASON_RISK["existing_expansion"],
      close_date_commitment: addDays(today, 8),
      justification: "Existing MSA from 2024 is in place. This is an add-on to their current compliance module. Procurement confirmed same PO process as original deal — 3 business days to execute.",
      deal_value_usd: 72000,
      status: "approved",
      approved_by: "manager",
      approved_at: new Date(today + "T11:30:00Z").toISOString(),
    },
    {
      id: "demo_ov_3",
      deal_id: "deal_003",
      deal_name: "VectorForm IoT — Analytics Platform",
      filed_by: "rep4@mi6demo.example.com",
      filed_by_name: "Demo AE 3",
      filed_at: new Date(today + "T14:00:00Z").toISOString(),
      reason: "legal_review",
      reason_label: OVERRIDE_REASON_LABELS["legal_review"],
      residual_risk_text: OVERRIDE_REASON_RISK["legal_review"],
      close_date_commitment: addDays(today, 18),
      justification: "Paper is exchanged. Their legal is on revision 2 of our MSA — two open items around data residency and indemnification clause. Both are standard. In-house counsel confirmed they can resolve within 10 business days.",
      deal_value_usd: 340000,
      status: "pending",
    },
    {
      id: "demo_ov_4",
      deal_id: "deal_004",
      deal_name: "Delta Industrial — Ops Module",
      filed_by: "rep5@mi6demo.example.com",
      filed_by_name: "Demo AE 4",
      filed_at: new Date(addDays(today, -20) + "T10:00:00Z").toISOString(),
      reason: "exec_sponsor",
      reason_label: OVERRIDE_REASON_LABELS["exec_sponsor"],
      residual_risk_text: OVERRIDE_REASON_RISK["exec_sponsor"],
      close_date_commitment: addDays(today, -5),
      justification: "CTO committed personally to closing before end of Q2. She cleared procurement path herself.",
      deal_value_usd: 120000,
      status: "resolved",
      resolution: "slipped",
      resolved_at: new Date(addDays(today, -4) + "T15:00:00Z").toISOString(),
    },
    {
      id: "demo_ov_5",
      deal_id: "deal_005",
      deal_name: "Cascade Automotive — Procurement Automation",
      filed_by: "kuksascend@outlook.com",
      filed_by_name: "Shobhit Sharma",
      filed_at: new Date(addDays(today, -30) + "T09:00:00Z").toISOString(),
      reason: "champion_guarantee",
      reason_label: OVERRIDE_REASON_LABELS["champion_guarantee"],
      residual_risk_text: OVERRIDE_REASON_RISK["champion_guarantee"],
      close_date_commitment: addDays(today, -10),
      justification: "Marcus (Director of Procurement) personally guaranteed the PO would be issued by the 15th. He has signed the statement of work and budget confirmation.",
      deal_value_usd: 95000,
      status: "resolved",
      resolution: "closed_on_date",
      resolved_at: new Date(addDays(today, -9) + "T14:00:00Z").toISOString(),
    },
    {
      id: "demo_ov_6",
      deal_id: "deal_006",
      deal_name: "Helios Devices — Platform Rollout",
      filed_by: "rep2@mi6demo.example.com",
      filed_by_name: "Demo AE 2",
      filed_at: new Date(today + "T08:00:00Z").toISOString(),
      reason: "internal_processes",
      reason_label: OVERRIDE_REASON_LABELS["internal_processes"],
      residual_risk_text: OVERRIDE_REASON_RISK["internal_processes"],
      close_date_commitment: addDays(today, 22),
      justification: "Technical evaluation is complete — passed SOC2 and GDPR review last week. Only remaining step is their quarterly vendor approval board which meets on the 28th. We are on the agenda.",
      deal_value_usd: 210000,
      status: "approved",
      approved_by: "manager",
      approved_at: new Date(today + "T08:30:00Z").toISOString(),
    },
  ];

  saveOverrides(demos);
}
